/**
 * ReferenceMergeDialog — generic «Объединить…» диалог для reference-справочников.
 *
 * AK-248 (1:1) → AK-271 (N→1). Дедупликация записей справочника: source-записи
 * объединяются в target (эталон) — все FK перепривязываются на target, sources
 * архивируются.
 *
 * Два режима (shape остаётся общим — болезни / вредители подключат позже):
 *
 *   1. **picker** (AK-248, обратная совместимость): фиксированный `source`,
 *      пользователь ВЫБИРАЕТ один `target` из `candidates`. Используется когда
 *      объединение запускается из строки-источника.
 *
 *   2. **batch** (AK-271): эталон (`target`) и список `sources` уже выбраны
 *      снаружи (мультивыбор в списке). Диалог — это confirm со СВОДНЫМ dry-run
 *      preview по всем источникам и атомарным N→1 merge.
 *
 * UX (3 принципа CLAUDE.md):
 *   1. функциональность — реальный preview counts (суммированный в batch);
 *   2. освоение — видно «что выживет / что поглотится» до подтверждения;
 *   3. усталость — loading на preview и merge, без мерцаний.
 *
 * DS-first: shadcn Dialog / Popover / Button / Input / Badge + lucide. Ни
 * одного inline hex.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowRight,
  Check,
  GitMerge,
  Loader2,
  Search,
  Star,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

/** Минимальная форма записи справочника, достаточная для merge-выбора. */
export interface MergeCandidate {
  id: string;
  /** Человекочитаемое имя для поиска/отображения. */
  label: string;
  /** Доп. подпись под именем (код / латынь) — опционально. */
  sublabel?: string;
  /** Архивные нельзя выбрать как target. */
  archived?: boolean;
}

/** Результат dry-run preview, нормализованный к UI-нуждам. */
export interface MergePreview {
  /** Сколько строк будет перепривязано на target. */
  totalRepointed: number;
  /** Сколько конфликтных строк source будет удалено (target уже имеет ключ). */
  totalDeleted: number;
  /**
   * AK-271: сколько источников реально поглотится (для batch). В picker-режиме
   * это всегда 1 — поле опционально.
   */
  sourceCount?: number;
}

interface BaseProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Тип сущности в винительном падеже для текстов — «культуру», «болезнь».
   * По умолчанию «запись».
   */
  entityAccusative?: string;
}

/**
 * Picker-режим (AK-248 → UX fix): пожиратель (target) фиксирован,
 * пользователь ВЫБИРАЕТ кого поглотить (source) из кандидатов.
 */
export interface ReferenceMergePickerProps extends BaseProps {
  mode?: 'picker';
  /** Культура-пожиратель (target): останется после объединения. */
  target: MergeCandidate | null;
  /** Все возможные жертвы (включая target/архивные — отфильтруем внутри). */
  candidates: MergeCandidate[];
  /** dry-run preview: реальные counts без персиста. */
  onPreview: (sourceId: string, targetId: string) => Promise<MergePreview>;
  /** Финальное объединение. */
  onConfirm: (sourceId: string, targetId: string) => Promise<void>;
}

/**
 * Batch-режим (AK-271): эталон + источники уже выбраны снаружи; диалог —
 * confirm со сводным preview.
 */
export interface ReferenceMergeBatchProps extends BaseProps {
  mode: 'batch';
  /** Эталон (target) — выживает. */
  target: MergeCandidate;
  /** Источники (будут архивированы). Минимум 1. */
  sources: MergeCandidate[];
  /** Сводный dry-run preview по всем источникам. */
  onPreviewBatch: (
    targetId: string,
    sourceIds: string[],
  ) => Promise<MergePreview>;
  /** Атомарный N→1 merge. */
  onConfirmBatch: (targetId: string, sourceIds: string[]) => Promise<void>;
}

export type ReferenceMergeDialogProps =
  | ReferenceMergePickerProps
  | ReferenceMergeBatchProps;

export function ReferenceMergeDialog(props: ReferenceMergeDialogProps) {
  if (props.mode === 'batch') {
    return <BatchMergeDialog {...props} />;
  }
  return <PickerMergeDialog {...props} />;
}

// ──────────────────────────────────────────────────────────────────────────
// Picker mode (AK-248 — unchanged behaviour, backward compatible)
// ──────────────────────────────────────────────────────────────────────────

function PickerMergeDialog({
  open,
  onOpenChange,
  target,
  candidates,
  entityAccusative = 'запись',
  onPreview,
  onConfirm,
}: ReferenceMergePickerProps) {
  const [sourceId, setSourceId] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);

  const onPreviewRef = useRef(onPreview);
  const onConfirmRef = useRef(onConfirm);
  useEffect(() => {
    onPreviewRef.current = onPreview;
  });
  useEffect(() => {
    onConfirmRef.current = onConfirm;
  });

  useEffect(() => {
    if (open) {
      setSourceId(null);
      setPickerSearch('');
      setPreview(null);
      setPreviewError(null);
      setMerging(false);
    }
  }, [open, target?.id]);

  const selectableVictims = useMemo(
    () => candidates.filter((c) => c.id !== target?.id && !c.archived),
    [candidates, target?.id],
  );

  const filteredVictims = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return selectableVictims;
    return selectableVictims.filter(
      (c) =>
        c.label.toLowerCase().includes(q) ||
        (c.sublabel?.toLowerCase().includes(q) ?? false),
    );
  }, [selectableVictims, pickerSearch]);

  const source = useMemo(
    () => selectableVictims.find((c) => c.id === sourceId) ?? null,
    [selectableVictims, sourceId],
  );

  useEffect(() => {
    if (!open || !target || !sourceId) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreview(null);
    onPreviewRef.current(sourceId, target.id)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setPreviewError(
            err instanceof Error ? err.message : 'Не удалось рассчитать предпросмотр',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target?.id, sourceId]);

  const handleConfirm = async () => {
    if (!target || !sourceId) return;
    setMerging(true);
    try {
      await onConfirmRef.current(sourceId, target.id);
      onOpenChange(false);
    } finally {
      setMerging(false);
    }
  };

  const handleSelectSource = (id: string) => {
    setSourceId(id);
    setPickerOpen(false);
  };

  const canMerge = Boolean(target && sourceId) && !merging;

  return (
    <Dialog open={open} onOpenChange={(next) => !merging && onOpenChange(next)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-primary" />
            Поглотить {entityAccusative}
          </DialogTitle>
          <DialogDescription>
            Выберите культуру для поглощения. Все посевы и операции жертвы
            перейдут к пожирателю, жертва будет архивирована.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Пожиратель / target (read-only, highlighted) */}
          <div className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              Пожиратель — останется
            </span>
            <div className="flex items-center gap-2 rounded-md border border-accent-brand/40 bg-accent-brand/10 px-3 py-2">
              <Star className="h-4 w-4 shrink-0 fill-accent-brand text-accent-brand" />
              <span className="truncate font-medium text-foreground">
                {target?.label ?? '—'}
              </span>
              {target?.sublabel && (
                <span className="truncate text-xs text-muted-foreground">
                  {target.sublabel}
                </span>
              )}
            </div>
          </div>

          {/* Жертва (search-select) */}
          <div className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              Кого поглотить — будет архивирована
            </span>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  disabled={merging}
                  className="w-full justify-between font-normal"
                >
                  {source ? (
                    <span className="truncate text-foreground">{source.label}</span>
                  ) : (
                    <span className="text-muted-foreground">
                      Выберите культуру…
                    </span>
                  )}
                  <Search className="ml-2 h-4 w-4 shrink-0 text-muted-foreground" />
                </Button>
              </PopoverTrigger>
              <PopoverContent
                align="start"
                className="w-[var(--radix-popover-trigger-width)] p-0"
              >
                <div className="border-b border-border p-2">
                  <Input
                    autoFocus
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    placeholder="Поиск…"
                    className="h-8"
                  />
                </div>
                <div className="max-h-60 overflow-y-auto p-1">
                  {filteredVictims.length === 0 ? (
                    <p className="px-2 py-6 text-center text-sm text-muted-foreground">
                      Ничего не найдено
                    </p>
                  ) : (
                    filteredVictims.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => handleSelectSource(c.id)}
                        className={cn(
                          'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground',
                          c.id === sourceId && 'bg-accent',
                        )}
                      >
                        <Check
                          className={cn(
                            'h-4 w-4 shrink-0',
                            c.id === sourceId ? 'opacity-100' : 'opacity-0',
                          )}
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-foreground">
                            {c.label}
                          </span>
                          {c.sublabel && (
                            <span className="block truncate text-xs text-muted-foreground">
                              {c.sublabel}
                            </span>
                          )}
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Preview */}
          {sourceId && (
            <div className="rounded-md border border-border bg-muted/30 p-3">
              {previewLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Рассчитываем, что будет перепривязано…
                </div>
              ) : previewError ? (
                <p className="text-sm text-destructive">{previewError}</p>
              ) : preview ? (
                <div className="space-y-2 text-sm">
                  <p className="text-muted-foreground">
                    Будет перепривязано{' '}
                    <Badge variant="secondary">{preview.totalRepointed}</Badge>{' '}
                    {pluralRows(preview.totalRepointed)}
                    {preview.totalDeleted > 0 && (
                      <>
                        {' '}· объединено дублей{' '}
                        <Badge variant="outline">{preview.totalDeleted}</Badge>
                      </>
                    )}
                    .
                  </p>
                  <p className="text-xs text-muted-foreground">
                    «{source?.label}» будет архивирована, данные перейдут в «{target?.label}».
                  </p>
                </div>
              ) : null}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={merging}
          >
            Отмена
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!canMerge}>
            {merging && <Loader2 className="h-4 w-4 animate-spin" />}
            Поглотить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Batch mode (AK-271 — эталон-первым + мультивыбор источников, N→1 atomic)
// ──────────────────────────────────────────────────────────────────────────

function BatchMergeDialog({
  open,
  onOpenChange,
  target,
  sources,
  entityAccusative = 'записи',
  onPreviewBatch,
  onConfirmBatch,
}: ReferenceMergeBatchProps) {
  const [preview, setPreview] = useState<MergePreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [merging, setMerging] = useState(false);

  // AK-248/271: ref-стабилизация колбэков (см. picker-режим выше).
  const onPreviewBatchRef = useRef(onPreviewBatch);
  const onConfirmBatchRef = useRef(onConfirmBatch);
  useEffect(() => {
    onPreviewBatchRef.current = onPreviewBatch;
  });
  useEffect(() => {
    onConfirmBatchRef.current = onConfirmBatch;
  });

  const sourceIds = useMemo(() => sources.map((s) => s.id), [sources]);
  // Стабильный ключ — чтобы preview не пересчитывался на каждый ререндер.
  const sourceKey = useMemo(() => [...sourceIds].sort().join(','), [sourceIds]);

  useEffect(() => {
    if (open) {
      setPreview(null);
      setPreviewError(null);
      setMerging(false);
    }
  }, [open, target.id, sourceKey]);

  // Сводный dry-run preview при открытии. Deps намеренно исключают колбэк
  // (стабилизирован через onPreviewBatchRef) и sourceIds (покрыт sourceKey).
  useEffect(() => {
    if (!open || sourceIds.length === 0) {
      setPreview(null);
      setPreviewError(null);
      return;
    }
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    setPreview(null);
    onPreviewBatchRef.current(target.id, sourceIds)
      .then((p) => {
        if (!cancelled) setPreview(p);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setPreviewError(
            err instanceof Error ? err.message : 'Не удалось рассчитать предпросмотр',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, target.id, sourceKey]);

  const handleConfirm = async () => {
    if (sourceIds.length === 0) return;
    setMerging(true);
    try {
      await onConfirmBatchRef.current(target.id, sourceIds);
      onOpenChange(false);
    } finally {
      setMerging(false);
    }
  };

  const canMerge = sourceIds.length >= 1 && !merging;
  const n = sources.length;

  return (
    <Dialog open={open} onOpenChange={(next) => !merging && onOpenChange(next)}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <GitMerge className="h-5 w-5 text-primary" />
            Объединить {entityAccusative}
          </DialogTitle>
          <DialogDescription>
            {n} {pluralSources(n)} будут объединены в эталон. Все посевы и
            операции перепривяжутся на эталон, источники архивируются.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Эталон (target) */}
          <div className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              Эталон — выживает
            </span>
            <div className="flex items-center gap-2 rounded-md border border-accent-brand/40 bg-accent-brand/10 px-3 py-2">
              <Star className="h-4 w-4 shrink-0 fill-accent-brand text-accent-brand" />
              <span className="truncate font-medium text-foreground">
                {target.label}
              </span>
              {target.sublabel && (
                <span className="truncate text-xs text-muted-foreground">
                  {target.sublabel}
                </span>
              )}
            </div>
          </div>

          {/* Источники (sources) */}
          <div className="space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">
              Источники — будут архивированы ({n})
            </span>
            <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-border bg-muted/30 p-2">
              {sources.map((s) => (
                <div
                  key={s.id}
                  className="flex items-center gap-2 rounded-sm px-2 py-1 text-sm"
                >
                  <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <span className="min-w-0 flex-1 truncate text-foreground">
                    {s.label}
                  </span>
                  {s.sublabel && (
                    <span className="shrink-0 truncate text-xs text-muted-foreground">
                      {s.sublabel}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Сводный preview */}
          <div className="rounded-md border border-border bg-muted/30 p-3">
            {previewLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Рассчитываем, что будет перепривязано…
              </div>
            ) : previewError ? (
              <p className="text-sm text-destructive">{previewError}</p>
            ) : preview ? (
              <div className="space-y-2 text-sm">
                <div className="flex flex-wrap items-center gap-1 text-muted-foreground">
                  Перепривяжется
                  <Badge variant="secondary">{preview.totalRepointed}</Badge>
                  {pluralRows(preview.totalRepointed)} из
                  <Badge variant="outline">{n}</Badge> {pluralSources(n)}
                  {preview.totalDeleted > 0 && (
                    <>
                      · объединится дублей
                      <Badge variant="outline">{preview.totalDeleted}</Badge>
                    </>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">
                  Эталон «{target.label}» сохранится, остальные уйдут в архив.
                </p>
              </div>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={merging}
          >
            Отмена
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!canMerge}>
            {merging && <Loader2 className="h-4 w-4 animate-spin" />}
            Объединить {n} в эталон
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function pluralRows(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'строку';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'строки';
  return 'строк';
}

function pluralSources(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'источник';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20))
    return 'источника';
  return 'источников';
}

export default ReferenceMergeDialog;
